import React, { useState } from 'react';
import { Alert, Typography, Badge, Button, Space } from 'antd';
import { ExclamationCircleOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import moment from 'moment';
import Spinner from '../../../../shared/designSystem/components/Spinner';

const { Text } = Typography;

const CampaignOrderIssuesAlerts = ({ issues = [], loading = false }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [dismissedIssues, setDismissedIssues] = useState(new Set());

  const visibleIssues = issues.filter((issue) => !dismissedIssues.has(issue.id));

  // Show loading state
  if (loading) {
    return (
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
          backgroundColor: '#f6f6f6',
          borderRadius: '6px',
          borderLeft: '4px solid #1890ff',
        }}
      >
        <Spinner size="sm" />
        <Text
          style={{
            marginLeft: 8,
            fontFamily: 'roboto',
            fontSize: '105%',
            color: '#636363',
          }}
        >
          Loading campaign issues...
        </Text>
      </div>
    );
  }

  if (!visibleIssues || visibleIssues.length === 0) {
    return null;
  }

  // Calculate issue counts by severity (only visible issues)
  const issuesCounts = visibleIssues.reduce((acc, issue) => {
    const severity = issue.severity;
    acc[severity] = (acc[severity] || 0) + 1;
    return acc;
  }, {});

  // Calculate grouped counts for display
  const errorCount =
    (issuesCounts.HIGH || 0) + (issuesCounts.CRITICAL || 0) + (issuesCounts.MEDIUM || 0);
  const warningCount = issuesCounts.LOW || 0;

  // Handle dismiss functionality
  const handleDismissIssue = (issueId) => {
    setDismissedIssues((prev) => new Set([...prev, issueId]));
  };

  const handleDismissAll = () => {
    setDismissedIssues(new Set(issues.map((issue) => issue.id)));
  };

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const totalIssues = visibleIssues.length;

  const getSeverityConfig = (severity) => {
    // HIGH, CRITICAL, MEDIUM → Error (red)
    // LOW → Warning (yellow)
    if (severity === 'HIGH' || severity === 'CRITICAL' || severity === 'MEDIUM') {
      return {
        type: 'error',
        icon: <ExclamationCircleOutlined style={{ fontSize: '20px' }} />,
        style: {
          borderColor: '#ff4d4f',
          borderWidth: '2px',
          backgroundColor: '#fff2f0',
        },
      };
    } else if (severity === 'LOW') {
      return {
        type: 'warning',
        icon: <ExclamationCircleOutlined style={{ fontSize: '20px' }} />,
        style: {
          borderColor: '#faad14',
          borderWidth: '2px',
          backgroundColor: '#fffbe6',
        },
      };
    }

    // Fallback to error for unknown severities
    return {
      type: 'error',
      icon: <ExclamationCircleOutlined style={{ fontSize: '20px' }} />,
      style: {
        borderColor: '#ff4d4f',
        borderWidth: '2px',
        backgroundColor: '#fff2f0',
      },
    };
  };

  const formatIssueMessage = (issue) => {
    const timeAgo = moment(issue.firstOccurredAt).fromNow();
    return (
      <div>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
          {issue.title}
        </div>
        <div style={{ fontSize: '14px', marginBottom: '4px' }}>{issue.message}</div>
        <div style={{ fontSize: '12px', color: '#666' }}>First occurred {timeAgo}</div>
      </div>
    );
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Header Row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          padding: '8px 16px',
          backgroundColor: '#f6f6f6',
          borderRadius: '6px',
          borderLeft: '4px solid #ff4d4f',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text
            style={{
              fontFamily: 'roboto',
              fontSize: '105%', // 14px equivalent (m size)
              fontWeight: 700, // bold
              color: '#636363', // dark color from system
              cursor: 'default',
            }}
          >
            Campaign Issues ({totalIssues})
          </Text>
          {errorCount > 0 && (
            <Badge
              count={`${errorCount} Error${errorCount > 1 ? 's' : ''}`}
              style={{
                backgroundColor: '#ff4d4f',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'normal',
              }}
            />
          )}
          {warningCount > 0 && (
            <Badge
              count={`${warningCount} Warning${warningCount > 1 ? 's' : ''}`}
              style={{
                backgroundColor: '#faad14',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'normal',
              }}
            />
          )}
        </div>

        <Space>
          <Button
            type="text"
            size="small"
            icon={collapsed ? <DownOutlined /> : <UpOutlined />}
            onClick={handleToggleCollapse}
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </Button>
          <Button type="text" size="small" onClick={handleDismissAll}>
            Dismiss All
          </Button>
        </Space>
      </div>

      {/* Individual Issue Alerts */}
      {!collapsed &&
        visibleIssues.map((issue) => {
          const severityConfig = getSeverityConfig(issue.severity);

          return (
            <Alert
              key={issue.id}
              message={formatIssueMessage(issue)}
              type={severityConfig.type}
              icon={severityConfig.icon}
              style={{
                marginBottom: 8,
                padding: '16px 24px',
                ...severityConfig.style,
              }}
              showIcon
              closable={false}
              action={
                <Button size="small" onClick={() => handleDismissIssue(issue.id)}>
                  Dismiss
                </Button>
              }
            />
          );
        })}
    </div>
  );
};

export default CampaignOrderIssuesAlerts;
